import React from 'react';
import { Link } from 'react-router-dom';

// Shared chrome for the two reader destinations. They used to look like different
// products: /readers had dark pills that read as buttons (Katia clicked "Find a
// reader" expecting it to do something, when it was marking the current page), and
// /readers/requests had a plain breadcrumb and no tabs at all. Same cream header,
// same tab row, same position on both — so moving between them reads as tabbing.

export type ReaderTabKey = 'find' | 'requests';

// The title and subhead are fixed, not per-page. They used to change with the tab,
// which made switching feel like landing on a different product rather than moving
// between two views of the same one. Only the underline moves now.
const TITLE = 'Book a reader.';
const SUBTITLE = 'Real people, real prep. Invite a reader, agree on a time, get a scene partner — and a sharper take.';

export function ReaderSectionHeader({
  active,
  requestCount = 0,
  actions,
}: {
  active: ReaderTabKey;
  requestCount?: number;
  actions?: React.ReactNode;
}) {
  const title = TITLE;
  const subtitle = SUBTITLE;
  const tabs: { key: ReaderTabKey; label: string; to: string; count?: number }[] = [
    { key: 'find', label: 'Find a reader', to: '/readers' },
    { key: 'requests', label: 'My requests', to: '/readers/requests', count: requestCount },
  ];
  return (
    <div
      style={{
        margin: '4px 0 22px',
        padding: '22px 24px 0',
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-beige)',
        border: '1px solid var(--hj-beige-line)',
        color: 'var(--hj-beige-ink)',
      }}
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>
            Reader Marketplace
          </div>
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 'clamp(32px, 4.6vw, 56px)', lineHeight: 1, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="m-0" style={{ margin: '12px 0 0', color: 'var(--hj-beige-ink)', fontSize: 15, maxWidth: 640, lineHeight: 1.5 }}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>

      {/* Underline tabs, flush with the bottom edge of the card: they sit on the
          boundary so the page below reads as the selected tab's panel. */}
      <div className="flex items-center gap-1" style={{ marginTop: 18 }} role="tablist">
        {tabs.map(t => {
          const on = t.key === active;
          return (
            <Link
              key={t.key}
              to={t.to}
              role="tab"
              aria-selected={on}
              aria-current={on ? 'page' : undefined}
              className="inline-flex items-center gap-2"
              style={{
                padding: '10px 14px',
                marginBottom: -1,
                borderBottom: `3px solid ${on ? 'var(--hj-beige-ink)' : 'transparent'}`,
                color: on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-ink-2)',
                fontSize: 14,
                fontWeight: on ? 700 : 500,
                textDecoration: 'none',
                minHeight: 44,
              }}
            >
              {t.label}
              {!!t.count && t.count > 0 && (
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999,
                    background: on ? 'var(--hj-beige-ink)' : 'rgba(0,0,0,0.1)',
                    color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink)',
                    fontSize: 11, fontWeight: 700,
                  }}
                >
                  {t.count}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
